// components/entities/tables/audits/AuditsTable.tsx
'use client';

import React, { useCallback, useMemo } from 'react';
import { useKernelStore } from '@/kernel/kernelStores';
import { EntityView } from '@/components/entities/EntityView';
import { Table, TableColumnDef } from '../../../portal/Table';
import type { InventoryEntityState } from '#types/types';
import { AlertTriangle, Box, Calendar, Gauge, ListChecks } from '@/components/ui/lucide-memo';

const translations = {
  en: {
    sortRisk: 'Risk score',
    sortCreated: 'Created date',
    sortProcedure: 'Procedure',
    entityHeader: 'Entity',
    procedureHeader: 'Procedure',
    scoreHeader: 'Score',
    issuesHeader: 'Issues',
    createdHeader: 'Created',
  },
  fr: {
    sortRisk: 'Score de risque',
    sortCreated: 'Date creation',
    sortProcedure: 'Procedure',
    entityHeader: 'Entite',
    procedureHeader: 'Procedure',
    scoreHeader: 'Score',
    issuesHeader: 'Problemes',
    createdHeader: 'Cree le',
  },
};

export const AuditsTable = React.memo(() => {
  const lang = useKernelStore((state) => state.language);
  const t = translations[lang];

  const columns = useMemo<TableColumnDef[]>(() => [
    { id: 'entity', icon: Box, minWidth: 200, header: t.entityHeader },
    { id: 'procedure', icon: ListChecks, minWidth: 180, header: t.procedureHeader },
    { id: 'score', icon: Gauge, minWidth: 80, header: t.scoreHeader },
    { id: 'issues', icon: AlertTriangle, minWidth: 80, header: t.issuesHeader },
    { id: 'created', icon: Calendar, minWidth: 100, header: t.createdHeader },
    { id: 'actions', minWidth: 240, header: '', align: 'right' },
  ], [t]);

  const sortOptions = useMemo(() => [
    { label: t.sortRisk, field: 'plausibilityScore' as const },
    { label: t.sortCreated, field: 'createdAt' as const },
    { label: t.sortProcedure, field: 'procedureName' as const },
  ], [t]);

  const renderRow = useCallback((
    audit: InventoryEntityState<'Audit'>,
    isExpanded: boolean,
    toggle: () => void,
    currentColumns?: TableColumnDef[],
  ) => (
    <div className="flex w-full flex-none flex-col">
      <EntityView
        type="Audit"
        entityId={audit.id}
        variant="row"
        onClick={toggle}
        columns={currentColumns}
        className={isExpanded ? 'bg-white/[0.02] border-white/10' : ''}
      />
      {isExpanded && (
        <EntityView
          type="Audit"
          entityId={audit.id}
          variant="page"
          pageHasEntityRowAbove
          className="h-auto border-b border-[#2A2A2A]"
        />
      )}
    </div>
  ), []);

  return (
    <Table
      entityType="Audit"
      supportsStats
      statsEntityType="Audit"
      columns={columns}
      assetLedgerTitle={lang === 'fr' ? 'Audits de donnees' : 'Data audits'}
      assetLedgerBreadcrumb="Inventory / Audits"
      sortOptions={sortOptions}
      renderRow={renderRow}
    />
  );
});
AuditsTable.displayName = 'AuditsTable';
